import type { VerticalLandingContent } from '@nexofacil/schemas';
import { buildWhatsappUrl } from '@nexofacil/utils/client';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock,
  Coins,
  Flame,
  Key,
  MapPin,
  Navigation,
  Phone,
  Radio,
  ShieldCheck,
  Truck,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { PublicBrokerSummary } from '@/lib/api';
import { brokerAvatarClass } from '@/lib/broker-avatar';
import { formatPhoneDisplay } from '@/lib/phone-format';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { LandingProps } from './types';

// =============================================================================
// PVLanding — design dedicado para proteção veicular. Inspirado no site da
// Risco Zero (primeiro parceiro PV) com melhorias de hierarquia visual,
// tipografia, contraste e fluxo de conversão. Estrutura:
//
//   1. Header sticky (navy/gold)
//   2. Hero split: copy + foto cover + form-card lateral
//   3. Faixa de risco (chips com riscos cobertos)
//   4. Grid de 8 coberturas com ícones
//   5. Como funciona (3 passos)
//   6. Sobre o consultor (com endereço opcional via aboutText)
//   7. Cidades atendidas (chips estilizados)
//   8. 3 cards grandes de diferenciais
//   9. FAQ
//  10. CTA final
//  11. Footer
//
// Listings de Property NÃO são renderizados aqui (PV não trabalha com
// "empreendimentos" no sentido MCMV). Se algum broker PV publicar planos
// no futuro, criamos uma nova seção dedicada.
// =============================================================================

const RISK_CHIPS: Array<{ icon: LucideIcon; label: string }> = [
  { icon: AlertTriangle, label: 'Colisões' },
  { icon: ShieldCheck, label: 'Roubo' },
  { icon: ShieldCheck, label: 'Furto' },
  { icon: Truck, label: 'Guincho' },
  { icon: Key, label: 'Chaveiro' },
  { icon: Users, label: 'Terceiros' },
];

const COVERAGES: Array<{ icon: LucideIcon; title: string; body: string }> = [
  {
    icon: ShieldCheck,
    title: 'Roubo e Furto',
    body: 'Indenização do veículo conforme valor de mercado em caso de perda total.',
  },
  {
    icon: Phone,
    title: 'Assistência 24h',
    body: 'Suporte 24 horas todos os dias do ano, em qualquer ponto do Brasil.',
  },
  {
    icon: Truck,
    title: 'Guincho',
    body: 'Reboque em caso de pane mecânica, elétrica ou colisão.',
  },
  {
    icon: Radio,
    title: 'Rastreamento',
    body: 'Monitoramento veicular contínuo, disponível conforme o plano.',
  },
  {
    icon: Users,
    title: 'Cobertura a Terceiros',
    body: 'Proteção para danos materiais e corporais a outros veículos e pessoas.',
  },
  {
    icon: Coins,
    title: 'Carro Reserva',
    body: 'Veículo substituto durante o reparo em situações previstas no plano.',
  },
  {
    icon: Flame,
    title: 'Incêndio',
    body: 'Cobertura em casos de incêndio acidental ou criminoso.',
  },
  {
    icon: Key,
    title: 'Chaveiro Emergencial',
    body: 'Atendimento de chaveiro 24h em caso de perda ou trava da chave.',
  },
];

// Cidades padrão (Grande Florianópolis). Brokers PV de outras regiões
// vão precisar customizar via pageContent (campo futuro).
const SERVICE_CITIES = [
  'São José',
  'Florianópolis',
  'Palhoça',
  'Biguaçu',
  'Kobrasol',
  'Campinas',
  'Barreiros',
  'Forquilhinhas',
];

export function PVLanding({ broker, content, onCta }: LandingProps) {
  return (
    <div className="min-h-screen bg-background">
      <BrokerHeader broker={broker} content={content} onCta={onCta} />
      <Hero broker={broker} content={content} onCta={onCta} />
      <RiskBanner />
      <CoveragesGrid />
      <HowItWorks content={content} />
      <AboutBroker broker={broker} content={content} />
      <CitiesSection />
      <FAQSection content={content} />
      <WhereWeAre broker={broker} />
      <FinalCTA content={content} onCta={onCta} />
      <Footer broker={broker} content={content} />
    </div>
  );
}

// ============================================================================
// Header
// ============================================================================

function BrokerHeader({
  broker,
  content,
  onCta,
}: {
  broker: PublicBrokerSummary;
  content: VerticalLandingContent;
  onCta: () => void;
}) {
  return (
    <header className="sticky top-0 z-30 border-b border-white/10 bg-primary/95 backdrop-blur supports-[backdrop-filter]:bg-primary/85">
      <div className="container flex h-20 items-center justify-between">
        <div className="flex items-center gap-3.5">
          {broker.avatarUrl ? (
            <img
              src={broker.avatarUrl}
              alt={broker.name}
              className={brokerAvatarClass(
                broker.pageContent.avatarShape,
                'h-14 w-14 ring-2 ring-[hsl(var(--brand-accent))]/50 shadow-md shadow-black/20',
              )}
            />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-base font-semibold text-white">
              {broker.name.charAt(0)}
            </div>
          )}
          <div className="leading-tight">
            <div className="text-base font-extrabold tracking-tight text-white md:text-lg">
              {broker.name}
            </div>
            <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--brand-accent))]">
              {content.brokerSubtitle}
            </div>
          </div>
        </div>
        <Button
          size="sm"
          onClick={onCta}
          className="bg-[hsl(var(--brand-accent))] text-primary hover:bg-[hsl(var(--brand-accent))]/90"
        >
          {content.brokerHeaderCta}
        </Button>
      </div>
    </header>
  );
}

// ============================================================================
// Hero — split layout: copy esquerda + card de cotação direita
// ============================================================================

function Hero({
  broker,
  content,
  onCta,
}: {
  broker: PublicBrokerSummary;
  content: VerticalLandingContent;
  onCta: () => void;
}) {
  const headline = broker.pageContent.heroHeadline ?? content.heroHeadlineDefault;
  const subheadline =
    broker.pageContent.heroSubheadline ?? content.heroSubheadlineDefault;
  const coverUrl = broker.pageContent.coverPhotoUrl;

  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground">
      {coverUrl ? (
        <>
          <img
            src={coverUrl}
            alt=""
            aria-hidden
            className="pointer-events-none absolute inset-0 h-full w-full object-cover object-[center_55%] opacity-85 md:opacity-70"
          />
          {/* Overlay mais leve no mobile pra foto da ponte respirar e reforçar
              identidade local. No desktop volta mais escuro pra contraste do
              texto sobre área maior. */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/55 via-primary/35 to-primary/65 md:from-primary/75 md:via-primary/55 md:to-primary/85" />
        </>
      ) : (
        <div className="pointer-events-none absolute inset-0 opacity-60">
          <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-[hsl(var(--brand-accent))]/10 blur-3xl" />
          <div className="absolute -bottom-40 right-0 h-[28rem] w-[28rem] rounded-full bg-[hsl(var(--brand-accent))]/10 blur-3xl" />
        </div>
      )}

      <div className="container relative grid gap-12 py-10 md:grid-cols-[1.2fr_1fr] md:items-center md:py-24 lg:py-28">
        <div>
          <h1 className="text-balance text-4xl font-extrabold leading-[1.05] tracking-tight md:text-5xl lg:text-[3.5rem]">
            {headline}
          </h1>

          <div className="mt-6 inline-flex items-start gap-3 rounded-2xl border border-[hsl(var(--brand-accent))]/40 bg-[hsl(var(--brand-accent))]/10 px-4 py-2.5 shadow-sm backdrop-blur">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--brand-accent))]" />
            <div className="leading-tight">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-white/75">
                Atendimento em
              </div>
              <div className="mt-0.5 text-sm font-bold text-[hsl(var(--brand-accent))]">
                São José e Grande Florianópolis
              </div>
            </div>
          </div>

          <p className="mt-6 max-w-xl text-balance text-base text-white/85 md:text-lg">
            {subheadline}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Button
              size="lg"
              onClick={onCta}
              className="h-12 bg-[hsl(var(--brand-accent))] px-7 text-base font-bold text-primary shadow-lg shadow-black/30 hover:bg-[hsl(var(--brand-accent))]/90"
            >
              {content.heroPrimaryCta}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <a
              href="#coberturas"
              className="inline-flex h-12 items-center justify-center rounded-md px-4 text-sm font-medium text-white/80 transition hover:text-white"
            >
              {content.heroSecondaryCta}
            </a>
          </div>

          <ul className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {content.trustBadges.slice(0, 3).map((label) => (
              <li
                key={label}
                className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/85 backdrop-blur"
              >
                <CheckCircle2 className="h-4 w-4 text-[hsl(var(--brand-accent))]" />
                {label}
              </li>
            ))}
          </ul>
        </div>

        <BrokerCard broker={broker} content={content} onCta={onCta} />
      </div>
    </section>
  );
}

function BrokerCard({
  broker,
  content,
  onCta,
}: {
  broker: PublicBrokerSummary;
  content: VerticalLandingContent;
  onCta: () => void;
}) {
  return (
    <div className="relative mx-auto w-full max-w-md md:mx-0">
      {/* Border glow dourado sutil */}
      <div className="absolute -inset-px rounded-[1.75rem] bg-gradient-to-br from-[hsl(var(--brand-accent))]/40 via-transparent to-[hsl(var(--brand-accent))]/20" />
      <div className="relative rounded-3xl bg-primary/60 p-7 backdrop-blur ring-1 ring-white/10">
        <div className="flex items-center gap-4">
          {broker.avatarUrl ? (
            <img
              src={broker.avatarUrl}
              alt={broker.name}
              className={brokerAvatarClass(
                broker.pageContent.avatarShape,
                'h-20 w-20 ring-2 ring-[hsl(var(--brand-accent))]/30',
              )}
            />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/10 text-2xl font-bold text-white">
              {broker.name.charAt(0)}
            </div>
          )}
          <div className="leading-tight">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--brand-accent))]">
              {content.brokerCardLabel}
            </div>
            <div className="mt-1 text-xl font-bold text-white">{broker.name}</div>
          </div>
        </div>
        {broker.bio && (
          <p className="mt-5 text-sm leading-relaxed text-white/80">{broker.bio}</p>
        )}

        <div className="my-6 h-px bg-white/10" />

        <div className="space-y-3">
          <div className="flex items-start gap-3 text-sm text-white/85">
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--brand-accent))]" />
            <span>Cotação rápida pelo WhatsApp · sem compromisso</span>
          </div>
          <div className="flex items-start gap-3 text-sm text-white/85">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--brand-accent))]" />
            <span>Explicação clara das condições antes da adesão</span>
          </div>
        </div>

        <Button
          onClick={onCta}
          className="mt-6 h-12 w-full bg-[hsl(var(--brand-accent))] text-base font-bold text-primary hover:bg-[hsl(var(--brand-accent))]/90"
        >
          {content.brokerCardCta}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Risk banner
// ============================================================================

function RiskBanner() {
  return (
    <section className="border-y bg-secondary/40">
      <div className="container py-10 md:py-12">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--brand-accent))]">
            Por que se proteger
          </p>
          <h2 className="mt-2 text-balance text-2xl font-bold leading-tight md:text-3xl">
            Seu veículo roda todo dia · o risco também
          </h2>
        </div>
        <ul className="mx-auto mt-8 flex max-w-3xl flex-wrap justify-center gap-2.5">
          {RISK_CHIPS.map((r) => (
            <li
              key={r.label}
              className="inline-flex items-center gap-2 rounded-full border bg-card px-3.5 py-1.5 text-sm font-medium shadow-sm"
            >
              <r.icon className="h-3.5 w-3.5 text-[hsl(var(--brand-accent))]" />
              {r.label}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ============================================================================
// Coverages grid
// ============================================================================

function CoveragesGrid() {
  return (
    <section id="coberturas" className="bg-background py-20 md:py-28">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--brand-accent))]">
            Proteção completa
          </p>
          <h2 className="mt-2 text-balance text-3xl font-bold leading-tight md:text-4xl">
            Coberturas incluídas
          </h2>
          <p className="mt-3 text-balance text-muted-foreground">
            Tudo que você precisa pra rodar tranquilo, em qualquer lugar do
            Brasil.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {COVERAGES.map((c) => (
            <div
              key={c.title}
              className="group relative rounded-2xl border bg-card p-6 transition-all hover:-translate-y-0.5 hover:border-[hsl(var(--brand-accent))]/40 hover:shadow-md"
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/5 text-primary group-hover:bg-[hsl(var(--brand-accent))]/15 group-hover:text-[hsl(var(--brand-accent))]">
                <c.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-base font-bold leading-tight text-foreground">
                {c.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {c.body}
              </p>
            </div>
          ))}
        </div>

        <p className="mx-auto mt-10 max-w-2xl text-center text-xs leading-relaxed text-muted-foreground">
          Coberturas variam conforme o plano contratado · todas as condições
          são detalhadas pelo consultor antes da adesão.
        </p>
      </div>
    </section>
  );
}

// ============================================================================
// Como funciona
// ============================================================================

function HowItWorks({ content }: { content: VerticalLandingContent }) {
  return (
    <section className="border-y bg-secondary/40 py-20 md:py-28">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--brand-accent))]">
            Como funciona
          </p>
          <h2 className="mt-2 text-balance text-3xl font-bold leading-tight md:text-4xl">
            {content.howItWorksTitle}
          </h2>
          <p className="mt-3 text-balance text-muted-foreground">
            {content.howItWorksSubtitle}
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {content.howItWorksSteps.map((s, i) => (
            <div
              key={s.title}
              className="relative rounded-2xl border bg-card p-7"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-base font-bold text-primary-foreground">
                {i + 1}
              </div>
              <h3 className="mt-5 text-xl font-bold leading-tight text-foreground">
                {s.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// About broker
// ============================================================================

function AboutBroker({
  broker,
  content,
}: {
  broker: PublicBrokerSummary;
  content: VerticalLandingContent;
}) {
  const about = broker.pageContent.aboutText;
  if (!about && !broker.bio) return null;

  return (
    <section className="bg-background py-20 md:py-28">
      <div className="container">
        <div className="grid gap-10 md:grid-cols-[280px_1fr] md:items-start">
          <div className="flex justify-center md:block">
            {broker.avatarUrl ? (
              <img
                src={broker.avatarUrl}
                alt={broker.name}
                className={cn(
                  'h-48 w-48 rounded-3xl shadow-xl shadow-primary/15 md:h-64 md:w-64',
                  broker.pageContent.avatarShape === 'square'
                    ? 'bg-white object-contain p-4'
                    : 'object-cover',
                )}
              />
            ) : (
              <div className="flex h-48 w-48 items-center justify-center rounded-3xl bg-primary text-6xl font-bold text-white md:h-64 md:w-64">
                {broker.name.charAt(0)}
              </div>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--brand-accent))]">
              {content.aboutTitle}
            </p>
            <h2 className="mt-2 text-3xl font-bold leading-tight md:text-4xl">
              {broker.name}
            </h2>
            {about ? (
              <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-muted-foreground">
                {about}
              </p>
            ) : broker.bio ? (
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                {broker.bio}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Cities served
// ============================================================================

function CitiesSection() {
  return (
    <section className="bg-primary/[0.03] py-16 md:py-20">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <Navigation className="mx-auto h-6 w-6 text-[hsl(var(--brand-accent))]" />
          <h2 className="mt-3 text-balance text-2xl font-bold leading-tight md:text-3xl">
            Atendemos em toda a Grande Florianópolis
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Atendimento humano local; assistência 24h em qualquer lugar do
            Brasil.
          </p>
        </div>
        <ul className="mx-auto mt-8 flex max-w-3xl flex-wrap justify-center gap-2">
          {SERVICE_CITIES.map((city) => (
            <li
              key={city}
              className="inline-flex items-center gap-1.5 rounded-full border-2 border-primary/10 bg-card px-4 py-2 text-sm font-medium text-foreground"
            >
              <MapPin className="h-3.5 w-3.5 text-[hsl(var(--brand-accent))]" />
              {city}
            </li>
          ))}
          <li className="inline-flex items-center gap-1.5 rounded-full border-2 border-dashed border-[hsl(var(--brand-accent))]/40 bg-[hsl(var(--brand-accent))]/5 px-4 py-2 text-sm font-medium text-[hsl(var(--brand-accent))]">
            e toda a Grande Florianópolis
          </li>
        </ul>
      </div>
    </section>
  );
}

// ============================================================================
// FAQ
// ============================================================================

function FAQSection({ content }: { content: VerticalLandingContent }) {
  return (
    <section className="border-t bg-secondary/40 py-20 md:py-28">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--brand-accent))]">
            FAQ
          </p>
          <h2 className="mt-2 text-balance text-3xl font-bold leading-tight md:text-4xl">
            {content.faqTitle}
          </h2>
          <p className="mt-3 text-balance text-muted-foreground">
            {content.faqSubtitle}
          </p>
        </div>
        <div className="mx-auto mt-10 max-w-3xl">
          <Accordion type="single" collapsible className="w-full">
            {content.faqItems.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger>{item.q}</AccordionTrigger>
                <AccordionContent>{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Final CTA
// ============================================================================

function FinalCTA({
  content,
  onCta,
}: {
  content: VerticalLandingContent;
  onCta: () => void;
}) {
  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-0 h-full w-[60rem] -translate-x-1/2 bg-gradient-to-b from-[hsl(var(--brand-accent))]/10 via-transparent to-transparent blur-3xl" />
      </div>
      <div className="container relative py-20 text-center md:py-28">
        <Wrench className="mx-auto h-8 w-8 text-[hsl(var(--brand-accent))]" />
        <h2 className="mx-auto mt-4 max-w-2xl text-balance text-3xl font-extrabold leading-tight md:text-4xl">
          {content.finalCtaTitle}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-balance text-white/85">
          {content.finalCtaSubtitle}
        </p>
        <Button
          size="lg"
          onClick={onCta}
          className="mt-8 h-12 bg-[hsl(var(--brand-accent))] px-8 text-base font-bold text-primary shadow-lg shadow-black/30 hover:bg-[hsl(var(--brand-accent))]/90"
        >
          {content.finalCtaButton}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </section>
  );
}

// ============================================================================
// Onde fica — endereço + CTAs (Maps + WhatsApp). Renderiza só se broker
// declarou address. Brokers que atendem só online ficam invisíveis aqui.
// ============================================================================

function WhereWeAre({ broker }: { broker: PublicBrokerSummary }) {
  const address = broker.pageContent.address;
  if (!address) return null;
  const mapLink =
    broker.pageContent.mapLinkUrl ??
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  // Mensagem sem saudação nominal · brokers PV são empresas.
  const waLink = buildWhatsappUrl({
    phoneE164: broker.whatsappNumber,
    message: 'Olá! Vim pelo site e quero solicitar uma cotação de proteção veicular.',
  });

  return (
    <section className="border-t bg-background py-20 md:py-24">
      <div className="container">
        <div className="grid gap-10 md:grid-cols-[1fr_1fr] md:items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--brand-accent))]">
              Onde nos encontrar
            </p>
            <h2 className="mt-2 text-balance text-3xl font-bold leading-tight md:text-4xl">
              Atendimento local em São José
            </h2>
            <p className="mt-3 max-w-md text-base leading-relaxed text-muted-foreground">
              Pra quem prefere conversar pessoalmente. A assistência 24h cobre
              todo o Brasil, mas a equipe está aqui na sua região.
            </p>

            <div className="mt-6 flex items-start gap-3 rounded-2xl border bg-card p-5">
              <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--brand-accent))]" />
              <div className="text-sm leading-relaxed">
                <div className="font-semibold text-foreground">Endereço</div>
                <div className="mt-1 whitespace-pre-line text-muted-foreground">
                  {address}
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <Button
                asChild
                className="h-11 flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <a href={mapLink} target="_blank" rel="noopener noreferrer">
                  <Navigation className="mr-2 h-4 w-4" />
                  Traçar rota
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="h-11 flex-1 border-[hsl(var(--brand-accent))]/40 text-foreground hover:bg-[hsl(var(--brand-accent))]/10"
              >
                <a href={waLink} target="_blank" rel="noopener noreferrer">
                  <Phone className="mr-2 h-4 w-4" />
                  Falar no WhatsApp
                </a>
              </Button>
            </div>
          </div>

          <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border bg-primary/5 md:aspect-square">
            {/* Google Maps embed sem API key, usando o modo `output=embed`
                clássico. Visual familiar pra usuário brasileiro. A query
                vem direto do endereço declarado. */}
            <iframe
              title="Mapa"
              className="h-full w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps?q=${encodeURIComponent(address.replace(/\n/g, ', '))}&output=embed&z=15`}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Footer expandido — endereço, telefone, regiões, copyright + nota legal
// ============================================================================

function Footer({
  broker,
  content,
}: {
  broker: PublicBrokerSummary;
  content: VerticalLandingContent;
}) {
  const year = new Date().getFullYear();

  return (
    <footer className="border-t bg-primary text-white">
      <div className="container py-12">
        <div className="grid gap-10 md:grid-cols-2">
          {/* Coluna 1 — marca + descrição (logo do broker, não do Nexo) */}
          <div>
            <div className="flex items-center gap-3">
              {broker.avatarUrl ? (
                <img
                  src={broker.avatarUrl}
                  alt={broker.name}
                  className={brokerAvatarClass(broker.pageContent.avatarShape, 'h-10 w-10')}
                />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/10 text-sm font-bold text-white">
                  {broker.name.charAt(0)}
                </div>
              )}
              <span className="text-sm font-bold leading-tight">{broker.name}</span>
            </div>
            {broker.bio && (
              <p className="mt-3 text-sm leading-relaxed text-white/70">
                {broker.bio}
              </p>
            )}
          </div>

          {/* Coluna 2 — regiões · contato fica na seção "Onde fica" + CTAs do hero */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--brand-accent))]">
              Regiões atendidas
            </p>
            <ul className="mt-3 grid grid-cols-2 gap-1 text-sm text-white/70">
              {SERVICE_CITIES.map((c) => (
                <li key={c}>{c}</li>
              ))}
              <li className="text-white/50">e mais</li>
            </ul>
          </div>
        </div>

        <div className="mt-10 border-t border-white/10 pt-6">
          <p className="text-[11px] leading-relaxed text-white/55">
            Proteção veicular é uma alternativa para determinados perfis de
            motoristas, com funcionamento próprio, regras, condições e
            regulamento específicos. Os detalhes das coberturas e exclusões
            são apresentados antes da contratação.
          </p>
          <div className="mt-5 flex flex-col items-center justify-between gap-3 text-xs text-white/50 sm:flex-row">
            <span>
              © {year} {broker.name} · {content.footerLabel}
            </span>
            <a
              href="/privacidade"
              className="underline transition-colors hover:text-white/80"
            >
              Política de privacidade
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

